import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabaseAdmin } from '../../../lib/supabase';

const gradientStyle = {
  background: 'linear-gradient(135deg, #1a56db 0%, #3b82f6 50%, #1d4ed8 100%)',
  border: 'none',
  color: '#ffffff',
};

const GradientButton = ({ children, onClick, iconName, size, className = '', disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg ${className}`}
    style={gradientStyle}
  >
    {iconName && <Icon name={iconName} size={size === 'sm' ? 14 : 16} color="#ffffff" />}
    {children}
  </button>
);

const InternalUserManagement = () => {
  const [internalUsers, setInternalUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchInternalUsers();
  }, []);

  const fetchInternalUsers = async () => {
    try {
      setIsLoading(true);
      const { data: profilesData, error: profilesError } = await supabaseAdmin?.from('user_profiles')?.select(`
          id,
          email,
          full_name,
          role,
          created_at
        `)?.eq('role', 'admin');

      if (profilesError) throw profilesError;
      setInternalUsers(profilesData || []);
    } catch (error) {
      console.error('Error fetching internal users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = internalUsers?.filter(user =>
    user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    user?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg shadow-md border border-border p-8">
        <div className="flex items-center justify-center">
          <Icon name="Loader2" className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading internal users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md border border-border">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="Shield" size={20} className="text-primary" />
              Internal User Management
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Manage admin and internal team members</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="search"
              placeholder="Search internal users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full sm:w-64"
            />
            <GradientButton iconName="UserPlus" size="sm" onClick={() => console.log('Add internal user')}>
              Add Admin
            </GradientButton>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Added Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {filteredUsers?.map((user) => (
              <tr key={user?.id} className="bg-primary/5 hover:bg-primary/10 transition-smooth">
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">{user?.email}</span>
                    <span className="text-xs text-muted-foreground">{user?.full_name || 'N/A'}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">Admin</span>
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {formatDate(user?.created_at)}
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">Active</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <GradientButton iconName="Eye" size="sm" onClick={() => console.log('View user:', user?.id)}>
                      View
                    </GradientButton>
                    <GradientButton iconName="Settings" size="sm" onClick={() => console.log('Manage permissions:', user?.id)}>
                      Permissions
                    </GradientButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-border">
        {filteredUsers?.map((user) => (
          <div key={user?.id} className="p-4 bg-primary/5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground mt-1">{user?.full_name || 'N/A'}</p>
              </div>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">Admin</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <span className="text-muted-foreground">Added:</span>
                <p className="text-foreground font-medium">{formatDate(user?.created_at)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>
                <p className="text-success font-medium">Active</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <GradientButton iconName="Eye" size="sm" onClick={() => console.log('View user:', user?.id)}>
                View
              </GradientButton>
              <GradientButton iconName="Settings" size="sm" onClick={() => console.log('Manage permissions:', user?.id)}>
                Permissions
              </GradientButton>
            </div>
          </div>
        ))}
      </div>

      {filteredUsers?.length === 0 && (
        <div className="p-8 md:p-12 text-center">
          <Icon name="Shield" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground">No internal users found</p>
        </div>
      )}
    </div>
  );
};

export default InternalUserManagement;